<script setup lang="ts">
import { computed, ref, toRaw } from 'vue';

// components
import Title from '@/components/Title.vue';
import PartyComponent from '@/components/PartyComponent.vue';
import TopControls from '@/components/TopControls.vue';
import BottomControls from '@/components/BottomControls.vue';
import TotalResultsComponent from '@/components/TotalResultsComponent.vue';
import HowToComponent from '@/components/HowToComponent.vue';
import DefenderComponent from '@/components/Defender.vue';
import SettingsComponent from '@/components/SettingsComponent.vue';
import ResultComponent from '@/components/ResultComponent.vue';
import ProgressComponent from '@/components/ProgressComponent.vue';
import OptionsComponent from '@/components/OptionsComponent.vue';

// libraries
import { Simulator } from '@/lib/trashsim/simulator';
import type { ProgressEvent } from '@/lib/trashsim/workers';

import Calculator, { ResultCases } from '@/lib/calculator/calculator';
import { Settings } from '@/lib/trashsim/settings';
import { NewWaveFromWave, type Options, type Wave } from '@/lib/trashsim';
import { NewParties, PartyMembers } from '@/lib/trashsim/party';
import { GetEntitiesFromVersion } from '@/lib/entityInfo';
import { LoadPrefill } from '@/lib/trashsim/loaders';
import DebugComponent from '@/components/DebugComponent.vue';
import type { Waves } from '@/lib/trashsim/waves';
import type { Ref } from 'vue';
import { onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { LoadAPI } from '@/lib/trashsim/loaders/api';

export declare interface Data {
    settings: Settings
    waves: Array<Wave>
    selectedWave: number
    simulator: Simulator
    active: boolean
    completedRounds: number
    options: Options
}

// settings
const settings = ref(new Settings())

const loadSettings = (newSettings?: Settings) => {
    if (newSettings === undefined) {
        return
    }

    newSettings.simulations = settings.value.simulations
    settings.value = newSettings
}

const simulate = async () => {
    console.debug("simulate triggered", settings.value.simulations);

    let entities = GetEntitiesFromVersion(settings.value.version);
    completedRounds.value = 0;
    active.value = true;

    simulator.simulate(toRaw(activeWave.value.parties), entities, settings.value, options.value).then(data => {
        activeWave.value.results = Calculator.calculate(data, entities, settings.value, false, null, activeWave.value.parties);
        activeWave.value.result = activeWave.value.results.cases[ResultCases.Average];
        truncateWaves();
    }).catch(err => {
        console.debug("simulation error", err)
    }).finally(() => {
        active.value = false
    });
}

const cancelSimulation = () => {
    active.value = false
    simulator.reset()
}

// options
const options: Ref<Options> = ref({workerCount: null})

// simulator stuff
const simulator = new Simulator()
const active = ref(false)
const completedRounds = ref(0)

// wave handlers
const waves: Ref<Waves> = ref([{ parties: NewParties(), results: null, result: null }])
const selectedWave = ref(0)
const activeWave = computed({
    get: () => waves.value[selectedWave.value],
    set: (value: Wave) => waves.value[selectedWave.value] = value
})

const newWave = () => {
    if (activeWave.value.result == null) {
        return
    }
    waves.value.push(NewWaveFromWave(toRaw(activeWave.value)))
    selectedWave.value++
}

const truncateWaves = () => {
    const next = selectedWave.value + 1
    if (next < waves.value.length) {
        waves.value.splice(next, waves.value.length - next);
    }   
}
const clearWaves = () => {
    selectedWave.value = 0
    waves.value = [{ parties: NewParties(), results: null, result: null }]
}

// mounted handler
const route = useRoute()
onMounted(() => {
    simulator.registerCallback((event: ProgressEvent) => { completedRounds.value += 1; });

    if (route.hash.startsWith("#prefill=")) {
        let prefill = LoadPrefill(route.hash);
        waves.value = prefill.waves;

        if (prefill.settings) {
            settings.value = prefill.settings
        }

        waves.value.forEach(wave => {
            for (const party of PartyMembers) {
                wave.parties[party].fleets.forEach((pfleet, index) => {
                    console.debug(`${party}[${index}]`, pfleet)
                    if (pfleet.API == null) {
                        return
                    }

                    LoadAPI(pfleet.API, party == "defenders" && index == 0).then(({fleet, settings}) => {
                        wave.parties[party].fleets[index] = fleet
                        loadSettings(settings)
                    }).catch(err => {
                        console.error("failed to load key from API", err);
                    })
                })
            }
        })
    }
    
    let api = route.query["SR_KEY"]?.toString();
    if (api != undefined) {
        LoadAPI(api, true).then(({fleet, settings}) => {
            activeWave.value.parties.defenders.fleets[0] = fleet
            loadSettings(settings)
        }).catch(err => {
            console.error("failed to load key from API", err);
        });
    }
})


// FIXME DEV ONLY SEED
// activeWave.value.parties.attackers.fleets[0].API = '{"coords":"2:291:10","characterClassId":2,"allianceClassId":0,"research":{"109":20,"110":20,"111":20,"114":12,"115":20,"117":20,"118":20},"defenses":{"401":{"amount":0,"weapon":0,"shield":0,"armor":0},"402":{"amount":0,"weapon":0,"shield":0,"armor":0},"403":{"amount":0,"weapon":0,"shield":0,"armor":0},"404":{"amount":0,"weapon":0,"shield":0,"armor":0},"405":{"amount":0,"weapon":0,"shield":0,"armor":0},"406":{"amount":0,"weapon":0,"shield":0,"armor":0},"407":{"amount":0,"weapon":0,"shield":0,"armor":0},"408":{"amount":0,"weapon":0,"shield":0,"armor":0}},"ships":{"202":{"amount":10,"weapon":0,"shield":0,"armor":0,"cargo":0.48479999999999995,"speed":0.22219999999999998,"fuel":0.12119999999999997},"203":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0.48479999999999995,"speed":0.22219999999999998,"fuel":0.12119999999999997},"204":{"amount":0,"weapon":0.0606,"shield":0.0606,"armor":0.0606,"cargo":0.0606,"speed":0.1818,"fuel":0.12119999999999997},"205":{"amount":0,"weapon":0.1515,"shield":0.1515,"armor":0.1515,"cargo":0.1515,"speed":0.2727,"fuel":0.12119999999999997},"206":{"amount":0,"weapon":0.2727,"shield":0.2727,"armor":0.2727,"cargo":0.2727,"speed":0.3939,"fuel":0.12119999999999997},"207":{"amount":0,"weapon":0.2424,"shield":0.2424,"armor":0.2424,"cargo":0.2424,"speed":0.3636,"fuel":0.12119999999999997},"208":{"amount":1,"weapon":0,"shield":0,"armor":0,"cargo":0.48479999999999995,"speed":0.22219999999999998,"fuel":0.12119999999999997},"209":{"amount":0,"weapon":0.505,"shield":0.505,"armor":0.505,"cargo":0.9897999999999999,"speed":0.7272,"fuel":0.12119999999999997},"210":{"amount":1000,"weapon":0,"shield":0,"armor":0,"cargo":0.48479999999999995,"speed":0.22219999999999998,"fuel":0.12119999999999997},"211":{"amount":0,"weapon":0.1212,"shield":0.1212,"armor":0.1212,"cargo":0.1212,"speed":0.2424,"fuel":0.12119999999999997},"212":{"amount":1500000,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0,"fuel":0.12119999999999997},"213":{"amount":1000000,"weapon":0.1212,"shield":0.1212,"armor":0.1212,"cargo":0.1212,"speed":0.24239999999999998,"fuel":0.12119999999999997},"214":{"amount":10000,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0,"fuel":0.12119999999999997},"215":{"amount":0,"weapon":0.18180000000000002,"shield":0.18180000000000002,"armor":0.18180000000000002,"cargo":0.18180000000000002,"speed":0.303,"fuel":0.12119999999999997},"217":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0,"fuel":0.12119999999999997},"218":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0.1212,"fuel":0.12119999999999997},"219":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0.1212,"fuel":0.12119999999999997}},"missiles":{"502":{"amount":0},"503":{"amount":0}},"bonuses":{"recycleAttackerFleet":0,"moonChanceIncrease":0,"lifeformProtection":0,"spaceDockExtender":0,"denCapacity":{"metal":2.4240000000000004,"crystal":2.4240000000000004,"deuterium":2.4240000000000004}}}'
activeWave.value.parties.attackers.fleets[0].API = '{"coords":"2:291:10","characterClassId":2,"allianceClassId":0,"research":{"109":20,"110":20,"111":20,"114":12,"115":20,"117":20,"118":20},"defenses":{"401":{"amount":0,"weapon":0,"shield":0,"armor":0},"402":{"amount":0,"weapon":0,"shield":0,"armor":0},"403":{"amount":0,"weapon":0,"shield":0,"armor":0},"404":{"amount":0,"weapon":0,"shield":0,"armor":0},"405":{"amount":0,"weapon":0,"shield":0,"armor":0},"406":{"amount":0,"weapon":0,"shield":0,"armor":0},"407":{"amount":0,"weapon":0,"shield":0,"armor":0},"408":{"amount":0,"weapon":0,"shield":0,"armor":0}},"ships":{"202":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0.48479999999999995,"speed":0.22219999999999998,"fuel":0.12119999999999997},"203":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0.48479999999999995,"speed":0.22219999999999998,"fuel":0.12119999999999997},"204":{"amount":0,"weapon":0.0606,"shield":0.0606,"armor":0.0606,"cargo":0.0606,"speed":0.1818,"fuel":0.12119999999999997},"205":{"amount":0,"weapon":0.1515,"shield":0.1515,"armor":0.1515,"cargo":0.1515,"speed":0.2727,"fuel":0.12119999999999997},"206":{"amount":0,"weapon":0.2727,"shield":0.2727,"armor":0.2727,"cargo":0.2727,"speed":0.3939,"fuel":0.12119999999999997},"207":{"amount":0,"weapon":0.2424,"shield":0.2424,"armor":0.2424,"cargo":0.2424,"speed":0.3636,"fuel":0.12119999999999997},"208":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0.48479999999999995,"speed":0.22219999999999998,"fuel":0.12119999999999997},"209":{"amount":0,"weapon":0.7070000000000001,"shield":0.7070000000000001,"armor":0.7070000000000001,"cargo":1.1918,"speed":0.9292,"fuel":0.12119999999999997},"210":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0.48479999999999995,"speed":0.22219999999999998,"fuel":0.12119999999999997},"211":{"amount":0,"weapon":0.2424,"shield":0.2424,"armor":0.2424,"cargo":0.2424,"speed":0.3636,"fuel":0.12119999999999997},"212":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0,"fuel":0.12119999999999997},"213":{"amount":1000000,"weapon":0.1212,"shield":0.1212,"armor":0.1212,"cargo":0.1212,"speed":0.24239999999999998,"fuel":0.12119999999999997},"214":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0,"fuel":0.12119999999999997},"215":{"amount":0,"weapon":0.18180000000000002,"shield":0.18180000000000002,"armor":0.18180000000000002,"cargo":0.18180000000000002,"speed":0.303,"fuel":0.12119999999999997},"217":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0,"fuel":0.12119999999999997},"218":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0.1212,"fuel":0.12119999999999997},"219":{"amount":0,"weapon":0,"shield":0,"armor":0,"cargo":0,"speed":0.1212,"fuel":0.12119999999999997}},"missiles":{"502":{"amount":0},"503":{"amount":0}},"bonuses":{"recycleAttackerFleet":0,"moonChanceIncrease":0,"lifeformProtection":0,"spaceDockExtender":0,"denCapacity":{"metal":3.232,"crystal":3.232,"deuterium":3.232}}}'
// activeWave.value.parties.attackers.fleets[0].ships['214'].amount = 10000
// activeWave.value.parties.defenders.fleets[0].defenses['401'].amount = 1000000
activeWave.value.parties.defenders.fleets[0].ships['204'].amount = 100000
activeWave.value.parties.defenders.fleets[0].resources.metal = 100000000

</script>

<template>
    <Title />
    <div id="page-home" class="page-element">
        <form id="simulate-form" @submit="simulate">
            <div id="parties" class="clearfix">
                <div class="party-column" v-for="(_, title) in activeWave.parties">
                    <PartyComponent
                      v-model="activeWave.parties[title]"
                      :defender="activeWave.parties.defenders.fleets[0]"
                      :settings="settings"
                      :result="activeWave.result"
                      :title="title"
                      @submit="simulate"
                      @loadSettings="loadSettings"
                />
                </div>
                <div id="planet-settings-column" class="party-column clearfix">
                    <!-- FIXME: need bindings -->
                    <TopControls :wave="activeWave" :settings="settings" @simulate="simulate"/>
                    <!-- may need to bind all the parties for the IPM -->
                    <DefenderComponent
                        v-model="activeWave.parties['defenders'].fleets[0]"
                        :result="activeWave.result"
                        :settings="settings"
                        @submit="simulate"
                    />
                    <SettingsComponent v-model="settings" />
                </div>
            </div>
            <BottomControls
                v-model="selectedWave"
                :waves="waves"
                @submit="simulate"
                @next="newWave"
                @clear="clearWaves"
            />
            <ResultComponent
                v-model="activeWave.result"
                :results="activeWave.results"
                :attacker="activeWave.parties['attackers'].fleets[0]"
                :defender="activeWave.parties['defenders'].fleets[0]"
                :settings="settings"
            />
            <TotalResultsComponent v-model="waves" />
        </form>
        <ProgressComponent 
            :active="active"
            :completedRounds="completedRounds"
            :totalRounds="settings.simulations"
            @cancel="cancelSimulation"
        />
        <HowToComponent />
        <OptionsComponent v-model="options" />
    </div>

    <DebugComponent v-model="activeWave" :waves="waves" :settings="settings" />
</template>